// import styles from "../settings.module.css";
import { useEffect, useState } from "react";
import { httprequest } from "../../../data/api";
import { toast } from "react-toastify";
import Table from "../../../components/Table/Table";
import PageLoader from "../../../components/PageLoader";
import { Grid } from "@mui/material";
import { AllowedScopes } from "../../../utils/DropDown";
import Button from "../../../components/Button";
import useWindowSize from "../../../hooks/useWindow";
import InputGroup from "../../../components/FormComponent/InputGroup";
import SelectGroup from "../../../components/FormComponent/SelectGroup";
import useStyles from "../styles";
import { Icon } from "@iconify/react/dist/iconify.js";
import Modal from "../../../components/Modal";
import { useLocation, useNavigate } from "react-router-dom";
import Ribbon from "../../../components/Ribbon";

const CUSTOM_FIELD_ENDPOINT = "/api/v1/custom-fields";

const CustomField = () => {
  const classes = useStyles();
  const location = useLocation();
  const navigate = useNavigate();

  const [customFields, setCustomFields] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [currentRow, setCurrentRow] = useState({});

  // Get action from URL parameters
  const urlParams = new URLSearchParams(location.search);
  const action = urlParams.get("action"); // 'create' or 'edit'
  const id = urlParams.get("id"); // for edit mode

  // Changed formData to an array to support multiple fields
  const [formFields, setFormFields] = useState([
    {
      name: "",
      scope: "",
      description: "",
      field_type: "",
      visible_to_users: false,
      user_editable: false
    }
  ]);

  const width = useWindowSize();
  const mobile = width < 600;

  // Update URL when changing views
  const updateUrl = (newAction = null, newId = null) => {
    const params = new URLSearchParams();
    if (newAction) {
      params.set("action", newAction);
      if (newId) {
        params.set("id", newId);
      }
    }
    navigate(`/settings?tab=custom-field&${params.toString()}`);
  };

  const resetForm = () => {
    setFormFields([
      {
        name: "",
        scope: "",
        description: "",
        field_type: "",
        visible_to_users: false,
        user_editable: false
      }
    ]);
    setIsEditing(false);
    setEditingId(null);
  };

  // Actual API calls
  const fetchCustomFields = async () => {
    setIsLoading(true);
    try {
      const response = await httprequest.get(CUSTOM_FIELD_ENDPOINT);
      setCustomFields(response?.data?.data?.fields || []);
      setIsLoading(false);
    } catch (error) {
      toast.error("Failed to fetch custom fields");
      setIsLoading(false);
    }
  };

  const handleDeleteRow = (row) => {
    setCurrentRow(row);
    setDeleteModal(!deleteModal);
  };

  const createCustomFields = async (fieldsData) => {
    setIsSubmitting(true);
    try {
      const response = await httprequest.post(`${CUSTOM_FIELD_ENDPOINT}/create`, {
        items: fieldsData
      });
      toast.success(response?.data?.message);
      fetchCustomFields();
      resetForm();
      updateUrl(); // Clear URL after successful creation
      setIsSubmitting(false);
    } catch (error) {
      toast.error("Failed to create custom fields");
      setIsSubmitting(false);
    }
  };

  const handleDelete = () => {
    setIsDeleting(true);
    httprequest.delete(`${CUSTOM_FIELD_ENDPOINT}/${currentRow?.id}`).then((res) => {
      setIsDeleting(false);
      toast.success(res?.data?.message);
      setDeleteModal(false);
      setCurrentRow({});
      fetchCustomFields();
    }).catch((err) => {
      setIsDeleting(false);
      toast.error(err?.response?.data?.message);
    });
  };

  const updateCustomField = async (id, data) => {
    setIsSubmitting(true);
    try {
      const response = await httprequest.patch(`${CUSTOM_FIELD_ENDPOINT}/${id}`, data);
      toast.success(response?.data?.message);
      resetForm();
      fetchCustomFields();
      updateUrl(); // Clear URL after successful update
      setIsSubmitting(false);
    } catch (error) {
      toast.error("Failed to update custom field");
      setIsSubmitting(false);
    }
  };

  // Handle input change for a specific field in the array
  const handleInputChange = (index, field, value) => {
    setFormFields(prev => {
      const updatedFields = [...prev];
      updatedFields[index] = {
        ...updatedFields[index],
        [field]: value
      };

      return updatedFields;
    });
  };

  // Handle checkbox change for a specific field in the array
  const handleCheckboxChange = (index, field) => {
    setFormFields(prev => {
      const updatedFields = [...prev];
      updatedFields[index] = {
        ...updatedFields[index],
        [field]: !updatedFields[index][field]
      };

      return updatedFields;
    });
  };

  // Add a new empty field
  const addAnotherField = () => {
    setFormFields(prev => [
      ...prev,
      {
        name: "",
        scope: "",
        description: "",
        field_type: "",
        visible_to_users: false,
        user_editable: false
      }
    ]);
  };

  // Remove a field by index
  const removeField = (index) => {
    if (formFields.length > 1) {
      setFormFields(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = () => {
    if (isEditing && editingId) {
      // For editing, we only work with the first field
      updateCustomField(editingId, formFields[0]);
    } else {
      // For creation, submit all fields
      createCustomFields(formFields);
    }
  };

  const handleEdit = (field) => {
    // When editing, set only one field in the form
    setFormFields([
      {
        name: field.name,
        scope: field.scope,
        description: field.description || "",
        field_type: field.field_type,
        visible_to_users: field.visible_to_users,
        user_editable: field.user_editable
      }
    ]);
    setIsEditing(true);
    setEditingId(field.id);
    updateUrl("edit", field.id);
  };

  const handleCreateNew = () => {
    resetForm();
    updateUrl("create");
  };

  const handleCancel = () => {
    resetForm();
    updateUrl();
  };

  // Load edit data when URL has edit action and id
  useEffect(() => {
    if (action === "edit" && id) {
      const fieldToEdit = customFields.find(field => field.id === id);
      if (fieldToEdit) {
        setFormFields([
          {
            name: fieldToEdit.name,
            scope: fieldToEdit.scope,
            description: fieldToEdit.description || "",
            field_type: fieldToEdit.field_type,
            visible_to_users: fieldToEdit.visible_to_users,
            user_editable: fieldToEdit.user_editable
          }
        ]);
        setIsEditing(true);
        setEditingId(id);
      }
    }
  }, [action, id, customFields]);

  // Handle create action from URL
  useEffect(() => {
    if (action === "create") {
      // Only set up create form if we're not already editing
      if (!isEditing) {
        resetForm();
      }
    }
  }, [action, isEditing]);

  const columns = [
    {
      name: "Name",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Name</p>}
          {row.name}
        </>
      )
    },
    {
      name: "Scope",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Scope</p>}
          {row.scope}
        </>
      )
    },
    {
      name: "Description",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Description</p>}
          {row.description || "N/A"}
        </>
      )
    },
    {
      name: "Field Type",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Field Type</p>}
          {row.field_type}
        </>
      )
    },
    {
      name: "User Visibility",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">User Visibility</p>}
          {row?.visible_to_users ? "Yes" : "No"}
        </>
      )
    },
    {
      name: "Editable by Users",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Editable by Users</p>}
          {row?.user_editable ? "Yes" : "No"}
        </>
      )
    },
    {
      name: "",
      center: "true",
      sortable: false,
      cell: (row) => (
        <div key={row?.id} className="table-action-container">
          <button
            className="table-action-icon"
            onClick={() => handleEdit(row)}
          >
            <Icon icon="heroicons:pencil-square-solid" />
          </button>
          <button
            className="table-action-icon delete-icon"
            onClick={() => handleDeleteRow(row)}
          >
            <Icon icon="mingcute:delete-2-line" />
          </button>
        </div>
      )
    }
  ];

  useEffect(() => {
    fetchCustomFields();
  }, []);

  // Determine current view based on URL parameters
  const isCreateView = action === "create";
  const isEditView = action === "edit" && id;

  return (
    <>
      <div className={classes.item}>
        <h4 className={classes.changepassword}>Custom Fields</h4>
        <Ribbon ribbonName="BETA FEATURE" />
        <div className={`text-align action_div gap-10 ${classes.headerContainer}`}>
          <div>
            <p className={classes.passwordtext}>
              {!isCreateView && !isEditView
                ? "See fields you have created"
                : isEditView
                  ? "Edit custom field"
                  : "Create fields for your school"
              }
            </p>
          </div>
          <div className="flex gap-10">
            {!isCreateView && !isEditView ? (
              <Button
                variant="primary"
                buttonSize="fluid"
                color="btndefault"
                buttonName="Create Custom Fields"
                onClick={handleCreateNew}
              />
            ) : (
              <Button
                variant="primaryoutline"
                buttonSize="fluid"
                color="btnfontprimary"
                buttonName="Back to List"
                onClick={handleCancel}
              />
            )}
          </div>
        </div>

        {!isCreateView && !isEditView ? (
          <div className={`margin-top-30 ${classes.tableContainer}`}>
            {isLoading ? (
              <PageLoader />
            ) : (
              <Table
                data={customFields}
                columns={columns}
                pagination
              />
            )}
          </div>
        ) : (
          <div className={classes.createForm}>
            <h3 className={classes.formTitle}>
              {isEditView ? "Edit Custom Field" : "Create Custom Fields"}
            </h3>

            {formFields.map((field, index) => (
              <div key={index} className={classes.fieldGroup}>
                {formFields.length > 1 && (
                  <div className={classes.fieldHeader}>
                    <h4 className={classes.fieldTitle}>Field {index + 1}</h4>
                    {formFields.length > 1 && (
                      <button
                        type="button"
                        className={classes.removeFieldButton}
                        onClick={() => removeField(index)}
                      >
                        <Icon icon="mdi:close" width={20} height={20} />
                      </button>
                    )}
                  </div>
                )}

                <Grid container spacing={2} className="input-padding">
                  <Grid item lg={6} md={12} sm={12} xs={12}>
                    <InputGroup
                      label="Name"
                      required
                      margin="normal"
                      placeHolder="Enter field name"
                      inputType="text"
                      value={field.name}
                      onChange={(e) => handleInputChange(index, "name", e.target.value)}
                      className={classes.inputField}
                    />
                  </Grid>
                  <Grid item lg={6} md={12} sm={12} xs={12}>
                    <SelectGroup
                      label="Scope"
                      required
                      children={
                        <select
                          onChange={(e) => handleInputChange(index, "scope", e.target.value)}
                          name="scope"
                          value={field.scope}
                        >
                          <option value="">---Select Scope ---</option>
                          {AllowedScopes?.map((data) => {
                            return (
                              <option value={data?.name} key={data?.name}>
                                {data?.text}
                              </option>
                            );
                          })}
                        </select>
                      }
                    />
                  </Grid>
                  <Grid item lg={6} md={12} sm={12} xs={12}>
                    <SelectGroup
                      label="Field Type"
                      required
                      children={
                        <select
                          value={field.field_type}
                          onChange={(e) => handleInputChange(index, "field_type", e.target.value)}
                          className={classes.selectField}
                        >
                          <option value="">--- Select Field Type ---</option>
                          <option value="text">Text</option>
                          <option value="number">Number</option>
                          <option value="date">Date</option>
                          <option value="textarea">Text Area</option>
                        </select>
                      }
                    />
                  </Grid>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <label className={classes.quelabel}>Description (optional)</label>
                    <div className={classes.textandimg}>
                      <textarea
                        type="text"
                        placeholder="Enter field description"
                        name="description"
                        value={field.description}
                        onChange={(e) => handleInputChange(index, "description", e.target.value)}
                      />
                    </div>
                  </Grid>

                  {/* Checkboxes */}
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <div className={classes.checkboxGroupItem}>
                      <label className={classes.checkboxLabel}>
                        <input
                          type="checkbox"
                          checked={field?.visible_to_users}
                          onChange={() => handleCheckboxChange(index, "visible_to_users")}
                          className={classes.checkbox}
                        />
                        User Visibility
                      </label>

                      {/* <label className={classes.checkboxLabel}>
                        <input
                          type="checkbox"
                          checked={field?.user_editable}
                          onChange={() => handleCheckboxChange(index, "user_editable")}
                          className={classes.checkbox}
                        />
                        Editable by users
                      </label> */}
                    </div>
                  </Grid>
                </Grid>

                {index === formFields.length - 1 && !isEditView && (
                  <div className={classes.addAnotherContainer}>
                    <button
                      type="button"
                      className={classes.addAnotherButton}
                      onClick={addAnotherField}
                    >
                      <Icon icon="mdi:plus" width={20} height={20} />
                      Add another Field
                    </button>
                  </div>
                )}
              </div>
            ))}

            <div className={`flex gap-10 ${classes.buttonContainer}`}>
              <Button
                variant="primary"
                buttonSize="medium"
                color="btndefault"
                buttonName={isEditView ? "Update Field" : "Create Fields"}
                onClick={handleSubmit}
                disabled={isSubmitting}
                loading={isSubmitting}
              />
              <Button
                variant="primaryoutline"
                buttonSize="medium"
                color="btnfontprimary"
                buttonName="Cancel"
                onClick={handleCancel}
              />
            </div>
          </div>
        )}
      </div>
      {/* Delete Modal */}
      <Modal
        title="Delete Custom Field"
        subtitle="Are you sure you want to delete this custom field? This action cannot be undone."
        modalContent={<div></div>}
        modalFooter={
          <>
            <Button
              type="submit"
              variant="danger"
              buttonName="Delete Custom Field"
              buttonSize="full"
              color="btndefault"
              onClick={handleDelete}
              isLoading={isDeleting}
            />
          </>
        }
        isopen={deleteModal}
        closeModal={handleDeleteRow}
      />
      {/* <ToastContainer /> */}
    </>
  );
};

export default CustomField;
